import { useEffect, useState } from "react";
import {
	Alert,
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	Divider,
	Grid2,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
	DetailHeader,
	DetailSection,
} from "@/controls/detail-panel/DetailPanel";
import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { useRemoveRollset } from "../hooks/useRollshopOperations";
import { getRollsetPositions } from "../models/Rollshop";
import type { RollsetListItem } from "../models/RollshopOperations";
import { COMPACT_DIALOG_SX } from "./compactDialog";
import { ReadOnlyField } from "./ReadOnlyField";

interface Props {
	rollset: RollsetListItem | null;
	onClose: () => void;
	onDeleted: () => void;
}

// "Top Roll" / "Bottom Roll" on common stands, "Roll 1" to "Roll 5" on the others.
const positionLabel = (label: string) =>
	/^\d+$/.test(label) ? `Roll ${label}` : `${label} Roll`;

export const DeleteRollsetModal = ({ rollset, onClose, onDeleted }: Props) => {
	const [errorMsg, setErrorMsg] = useState("");
	const { removeRollset, isRemovingRollset } = useRemoveRollset({
		onSuccess: onDeleted,
		onError: (e) => setErrorMsg(e.message),
	});

	useEffect(() => {
		if (rollset) setErrorMsg("");
	}, [rollset]);

	const positions = rollset ? getRollsetPositions(rollset) : [];
	const hasRolls = positions.some((p) => p.rollId != null);

	return (
		<Dialog open={rollset !== null} onClose={onClose} maxWidth="sm" fullWidth>
			<DetailHeader
				title="Delete Rollset"
				isSaving={isRemovingRollset}
				onClose={onClose}
			/>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					if (rollset && !hasRolls) removeRollset(rollset.Id);
				}}
			>
				<DialogContent sx={{ p: 2, ...COMPACT_DIALOG_SX }}>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
						<DetailSection title="Rollset" icon={<InfoOutlinedIcon />}>
							<Grid2 container spacing={1}>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<ReadOnlyField label="Rollset" value={rollset?.Name} />
								</Grid2>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<ReadOnlyField label="Stand" value={rollset?.StandName} />
								</Grid2>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<ReadOnlyField
										label="Cartridge"
										value={rollset?.CartridgeName}
									/>
								</Grid2>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<ReadOnlyField label="Barrel" value={rollset?.BarrelName} />
								</Grid2>
								{positions.map((p) => (
									<Grid2 key={p.position} size={{ xs: 12, md: 6 }}>
										<ReadOnlyField
											label={positionLabel(p.label)}
											value={p.rollName}
										/>
									</Grid2>
								))}
							</Grid2>
						</DetailSection>
						{hasRolls ? (
							<Alert severity="error">
								Rollset can not be deleted because it has roll/s assigned.
							</Alert>
						) : (
							errorMsg && <Alert severity="error">{errorMsg}</Alert>
						)}
					</Box>
				</DialogContent>
				<Divider />
				<DialogActions sx={{ px: 2, py: 1.5 }}>
					<FooterModalButtons
						isSubmitLoading={isRemovingRollset}
						disabled={hasRolls}
						onHide={onClose}
						buttonColor="error"
						buttonLabel="Delete"
					/>
				</DialogActions>
			</form>
		</Dialog>
	);
};
